import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
	FormComponent,
	InvalidTypeDirective,
	InputErrorComponent,
	RadioGroupComponent,
	RadioOption,
} from '@woolworthsnz/form';
import { BreakPointService } from '@woolworthsnz/styleguide';
import { BonusProductModel, BonusProductSelectedRequest } from '@woolworthsnz/trader-api';
import { NgIf } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'product-bonus-product',
	templateUrl: './bonus-product.component.html',
	styleUrls: ['./bonus-product.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		FormsModule,
		FormComponent,
		ReactiveFormsModule,
		RadioGroupComponent,
		InputErrorComponent,
		InvalidTypeDirective,
	],
})
export class BonusProductComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() bonusProductModel: BonusProductModel;
	@Input() affirmativeActionLabel = 'Yes, please send to me with my order';
	@Input() negativeActionLabel = 'No Thanks';
	@Output() productSelectionEmitter: EventEmitter<BonusProductSelectedRequest> = new EventEmitter();
	private destroyed$: Subject<boolean> = new Subject();
	isMobile: boolean;
	form: FormGroup;

	get name(): string | undefined {
		return this.bonusProductModel?.name;
	}

	get orientation(): string {
		return this.isMobile ? 'vertical' : 'horizontal';
	}

	get uniqueIdentifier(): string {
		return `bpSend${this.bonusProductModel?.id || -1}`;
	}

	get message(): string | undefined {
		return this.bonusProductModel?.showMessage ? this.bonusProductModel?.message : '';
	}

	get options(): RadioOption[] {
		return [
			{
				value: 'True',
				label: this.affirmativeActionLabel,
			},
			{
				value: 'False',
				label: this.negativeActionLabel,
			},
		];
	}

	get defaultValue(): string {
		return this.bonusProductModel?.bonusProductSelected ? 'True' : 'False';
	}

	@Input()
	set serverError(val: boolean) {
		this.form?.controls[this.uniqueIdentifier].setErrors({ serverError: val });
	}

	constructor(private formBuilder: FormBuilder, private breakpointService: BreakPointService) {}

	ngOnInit(): void {
		this.form = this.formBuilder.group({});
		this.breakpointService.isSmallDevice$.pipe(takeUntil(this.destroyed$)).subscribe((isMobile) => {
			this.isMobile = isMobile;
		});
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	ngAfterViewInit(): void {
		// let the child components add to the form before subscribing
		this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(this.handleFormValueChange);
	}

	handleFormValueChange = (e: any): void => {
		const r: BonusProductSelectedRequest = {
			selected: e[this.uniqueIdentifier] === 'True' ? true : false,
			id: this.bonusProductModel.id,
		};
		this.productSelectionEmitter.emit(r);
	};
}
